import { useEffect } from "react";
import { Link, useLocation } from "react-router";
import { PageShell } from "../../ui/PageShell";

/** What the sign-up form hands this page (react-router location state). */
export interface SignUpDoneState {
  email: string;
  displayName: string;
  /**
   * signedIn: signed in with the chosen avatar; verifyThenSignIn: open the emailed link, then sign in (the avatar choice
   * is kept and applied then); signInLater: sign in to save the avatar (local development, no email); avatarFailed:
   * signed in, but the avatar could not be saved.
   */
  outcome: "signedIn" | "verifyThenSignIn" | "signInLater" | "avatarFailed";
  /** A verification link was emailed (every environment but local development). */
  emailSent: boolean;
  avatarError?: string;
}

/**
 * Founder 2026-10-27: after "Create account" the visitor lands here instead of on a message at the top of the long
 * sign-up form ("you have no way of knowing that unless you scroll up").
 */
export function SignUpDonePage() {
  const state = (useLocation().state ?? null) as SignUpDoneState | null;
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <PageShell title="Account created" narrow>
      <SignUpDoneView state={state} />
    </PageShell>
  );
}

/** Presentational (server-rendered in unit tests). */
export function SignUpDoneView({ state }: { state: SignUpDoneState | null }) {
  if (!state) {
    return (
      <div className="card stack" data-testid="signup-done" data-outcome="none">
        <h1 style={{ margin: 0 }}>Your account</h1>
        <p style={{ margin: 0 }}>Sign in or manage your account on the Account page.</p>
        <div className="row">
          <Link to="/account" className="btn btn-primary">
            Account
          </Link>
          <Link to="/" className="btn">
            Back to the town
          </Link>
        </div>
      </div>
    );
  }
  const { email, displayName, outcome, emailSent } = state;
  const verifyNote = emailSent ? (
    <p style={{ margin: 0 }}>
      We sent a verification link to <strong>{email}</strong> (check your spam folder too). Open it to verify your email:
      buying and earning coins need a verified email.
    </p>
  ) : null;
  return (
    <div className="card stack" data-testid="signup-done" data-outcome={outcome}>
      {outcome === "verifyThenSignIn" ? (
        <>
          <h1 style={{ margin: 0 }}>Check your email</h1>
          <p style={{ margin: 0 }}>
            Your account <strong>{displayName}</strong> is created. We sent a verification link to <strong>{email}</strong>{" "}
            (check your spam folder too). Open it, then sign in.
          </p>
          <p style={{ margin: 0 }}>Your avatar choice is kept and applied when you sign in.</p>
          <div className="row">
            <Link to="/account" className="btn btn-primary">
              Sign in
            </Link>
            <Link to="/" className="btn">
              Back to the town
            </Link>
          </div>
        </>
      ) : outcome === "signInLater" ? (
        <>
          <h1 style={{ margin: 0 }}>Account created</h1>
          <p style={{ margin: 0 }}>Your account {displayName} is created. Sign in and your avatar choice will be saved.</p>
          <div className="row">
            <Link to="/account" className="btn btn-primary">
              Sign in
            </Link>
            <Link to="/" className="btn">
              Back to the town
            </Link>
          </div>
        </>
      ) : (
        <>
          <h1 style={{ margin: 0 }}>Welcome to Townsy, {displayName}!</h1>
          {outcome === "signedIn" ? (
            <p style={{ margin: 0 }}>Account created and signed in with your avatar.</p>
          ) : (
            <>
              <p style={{ margin: 0 }}>Account created and signed in.</p>
              <div className="notice notice-warning small" role="status">
                Your avatar could not be saved ({state.avatarError ?? "an error"}). Use "Edit avatar" on your Account page to try
                again.
              </div>
            </>
          )}
          {verifyNote}
          <div className="row">
            <Link to="/" className="btn btn-primary">
              Go to the town
            </Link>
            <Link to="/account" className="btn">
              Your account
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
